// ═══════════════════════════════════════════════════════════════════════════
// visualSelection.jsx — Stakeholder-grade CLM Architecture Visualization
// ═══════════════════════════════════════════════════════════════════════════
// Major v10 enhancements over v9:
//   1. Differentiated shape language per node role
//      (hub · gateway · application · database · external · physical · ESB)
//   2. ISA-95 swim-lane backgrounds with subtle gradients + level chips
//   3. Sized nodes — system importance reflected in width × height
//   4. Three view presets:
//        — Executive  (high contrast, fewer details, no L0/L1)
//        — Implementer (everything visible, animated)
//        — Print/Export (white background, no animation, hi-contrast)
//   5. Proper legend panel (shapes, line types, severity colors, levels)
//   6. Theme integration — uses shared theme.js
//   7. Cross-tab nav — clicking a node lets you jump to LLD for an interface
//   8. Taxonomy integration — interface badges show payload type colors
//   9. Better edges — orthogonal routing for verticals, smooth bezier
//      for cross-lane; bidirectional arrows; thickness scaled to filter
//  10. Mini-map overview
//  11. Color-blind safe palette option
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useCallback, useMemo, useRef, useEffect } from "react";
import {
  IFACES, GRP_META, getInterfaceProgress,
} from "./IfacesData";
import { getProtocolsByInterface } from "./IF_Protocols";
import { useFilteredInterfaces, FilterPanel } from "./filterSystem";
import { T, FONTS } from "./theme";
import { useNavigation } from "./navigation";
import {
  getAgentsForInterface, getBenefitsForInterface, getFlowsForInterface,
} from "./crossRefs";

// ═══════════════════════════════════════════════════════════════════════════
// 1 · ISA-95 SWIM LANES (canvas y-bands)  ·  REDESIGNED for clean, no-overlap layout
// ═══════════════════════════════════════════════════════════════════════════
// Key layout principles in this redesign:
//   · A LEFT GUTTER (x 0–LABEL_W) is reserved exclusively for lane labels.
//     Nodes never enter it, so labels and nodes can't collide.
//   · A DEDICATED BUS BAND sits BETWEEN L4 and L3 as its own horizontal strip,
//     so the enterprise service bus no longer overlaps any node.
//   · Lanes are taller (more vertical breathing room) and the canvas is wider,
//     giving every node generous spacing with zero horizontal overlap.
//
const LABEL_W      = 150;                 // left gutter reserved for lane labels
const NODE_AREA_X0 = LABEL_W + 30;        // nodes start here
const LANE_TOP     = 30;                  // first lane top
const LANE_H       = 175;                 // standard lane height (was 150)
const BUS_BAND_H   = 70;                  // dedicated band for the ESB

// y-position bookkeeping (sequential bands, no overlaps by construction)
const L4_Y   = LANE_TOP;                              // Enterprise
const BUS_Y0 = L4_Y + LANE_H;                         // bus band top
const L3_Y   = BUS_Y0 + BUS_BAND_H;                   // Manufacturing exec
const L2_Y   = L3_Y + LANE_H;                         // Process control
const L1_Y   = L2_Y + LANE_H;                         // Sensors / devices
const L0_Y   = L1_Y + LANE_H;                         // Physical
const L0_H   = 130;

const ISA95_LANES = [
  { id: "L4_ERP",     y: L4_Y, h: LANE_H, level: "L4", label: "ENTERPRISE",          subtitle: "ERP · PLM · Cloud · partners", color: T.gold,   icon: "🏢" },
  { id: "L3_MES",     y: L3_Y, h: LANE_H, level: "L3", label: "MFG EXECUTION",       subtitle: "MES · APS · gateways · eng",   color: T.blue,   icon: "⚙️" },
  { id: "L2_CONTROL", y: L2_Y, h: LANE_H, level: "L2", label: "PROCESS CONTROL",     subtitle: "Edge · HMI · QM lab",          color: T.orange, icon: "🎛️" },
  { id: "L1_SENSORS", y: L1_Y, h: LANE_H, level: "L1", label: "SENSORS / DEVICES",   subtitle: "PLCs · CNC · robots · IIoT",   color: T.teal,   icon: "🌡️" },
  { id: "L0_PHYSICAL",y: L0_Y, h: L0_H,   level: "L0", label: "PHYSICAL PROCESS",    subtitle: "Conveyors · WIP · production", color: T.steel,  icon: "🏭" },
];
const LEVEL_BY_KEY = Object.fromEntries(ISA95_LANES.map(l => [l.id, l]));
// Convenience: get lane centre-y for a level
const laneCenterY = (level) => {
  const l = LEVEL_BY_KEY[level];
  if (!l) return 0;
  return l.y + l.h / 2;
};

// ═══════════════════════════════════════════════════════════════════════════
// 2 · NODE-SHAPE LANGUAGE — differentiated per role
// ═══════════════════════════════════════════════════════════════════════════
//
//   hub          rounded-rectangle (large)  — primary systems (PLM, ERP)
//   application  rounded-rectangle (medium) — secondary apps
//   gateway      hexagon                    — integration gateways (AIG, T4S, CN4S, CN4T)
//   bus          horizontal capsule (wide)  — the ESB / message broker
//   database     cylinder                   — data stores
//   external     dashed rectangle           — outside-the-firewall systems
//   physical     trapezoid                  — physical assets (conveyors, processes)
//   device       octagon                    — PLCs, CNC, robots, sensors
//
const NODE_ROLES = {
  hub:         { label: "Primary system" },
  application: { label: "Application"    },
  gateway:     { label: "Integration gateway / adapter" },
  bus:         { label: "Enterprise service bus"   },
  database:    { label: "Data store"     },
  external:    { label: "External system / partner" },
  physical:    { label: "Physical process" },
  device:      { label: "Control device" },
};

// Default node sizes per role
const NODE_SIZE = {
  hub:         { w: 168, h: 84 },
  application: { w: 150, h: 76 },
  gateway:     { w: 120, h: 76 },
  bus:         { w: 1180, h: 54 },
  database:    { w: 130, h: 84 },
  external:    { w: 148, h: 76 },
  physical:    { w: 160, h: 70 },
  device:      { w: 132, h: 76 },
};

// ═══════════════════════════════════════════════════════════════════════════
// 3 · NODES — placed on the swim-lane canvas (NO OVERLAPS by construction)
// ═══════════════════════════════════════════════════════════════════════════
// Horizontal slot grid — every node sits on one of these column centres, giving
// even spacing. Columns are ≥220px apart so even 168-wide hubs keep a clean
// ≥52px gutter between neighbours.
//
//   col:   0     1     2     3     4     5     6
//   x:    270   490   710   930  1150  1370  1590
//
const COL = [270, 490, 710, 930, 1150, 1370, 1590];

const CANVAS_W = 1740;
const CANVAS_H = L0_Y + L0_H + 40;
const BUS_CY   = BUS_Y0 + BUS_BAND_H / 2;   // bus vertical centre (its own band)


const NODES = {
  // L4 · Enterprise
  PLM:      { x: COL[0], y: laneCenterY("L4_ERP"),     role: "hub",         color: T.cyan,   label: "Teamcenter",      sub: "PLM",                 version: "2512",   level: "L4_ERP",     icon: "📐", standard: "ISA-95 L4",
              businessObjects: ["Item","BOM","BOP","ECO","WorkPackage","Dataset","Mfg0BvrOperation","MEEquipment","Requirement","ChangeOrder"],
              interfaces: ["IF-001","IF-002","IF-003","IF-004","IF-005","IF-006","IF-012","IF-013","IF-015","IF-024","IF-026","IF-027","IF-032","IF-035","IF-037","IF-040"] },
  ERP:      { x: COL[2], y: laneCenterY("L4_ERP"),     role: "hub",         color: T.gold,   label: "SAP S/4HANA",     sub: "ERP",                 version: "2023",   level: "L4_ERP",     icon: "💼", standard: "ISA-95 L4",
              businessObjects: ["MaterialMaster","ProductionOrder","GoodsIssue","Confirmation","InspectionLot","QualityNotification","PurchaseOrder","Routing","BOM","CO","PMOrder","SalesOrder"],
              interfaces: ["IF-007","IF-008","IF-009","IF-010","IF-011","IF-012","IF-013","IF-014","IF-016","IF-023","IF-026","IF-029","IF-032","IF-034","IF-037"] },
  HUB:      { x: COL[4], y: laneCenterY("L4_ERP"),     role: "hub",         color: T.purple, label: "Insights Hub",    sub: "Cloud analytics / AI",version: "2024",   level: "L4_ERP",     icon: "☁️", standard: "ISA-95 L4",
              businessObjects: ["Timeseries","AssetModel","MLModel","Prediction","Alert","Dashboard","KPI","DigitalTwin"],
              interfaces: ["IF-017","IF-022","IF-023","IF-024","IF-025","IF-028","IF-030","IF-036"] },
  SUPPLIER: { x: COL[5], y: laneCenterY("L4_ERP"),     role: "external",    color: T.amber,  label: "Supplier portal", sub: "EDI / B2B (AS2)",     level: "L4_ERP",     icon: "🚚", standard: "External",
              businessObjects: ["PurchaseOrder","ASN","Invoice","MaterialData","QualityCertificate"],
              interfaces: ["IF-014"] },
  CUSTOMER: { x: COL[6], y: laneCenterY("L4_ERP"),     role: "external",    color: T.green,  label: "Customer portal", sub: "Orders · status · NPS",                  level: "L4_ERP",     icon: "🏢", standard: "External",
              businessObjects: ["SalesOrder","OrderStatus","Warranty","NPS","Complaint"],
              interfaces: [] },

  // ESB · the bus stretches horizontally across L3 top
  ESB:      { x: (COL[0]+COL[6])/2, y: BUS_CY,             role: "bus",         color: T.green,  label: "Enterprise Service Bus",     sub: "Kafka · schema-registered · DLQ-backed",     level: "L3_MES",     icon: "🚌", standard: "Kafka / Confluent",
              businessObjects: [],
              interfaces: [] },

  // L3 · Manufacturing execution
  AIG:      { x: COL[0], y: laneCenterY("L3_MES"),     role: "gateway",     color: T.blue,   label: "AIG",             sub: "TC ↔ all",            version: "2024",   level: "L3_MES",     icon: "🔄", standard: "Siemens AIG",
              businessObjects: ["Item","MaterialMaster","BOM","CSBOM","BOP","Routing","ECO","ChangeOrder","Document","DMS","Feedback","Scorecard"],
              interfaces: ["IF-012","IF-013","IF-014","IF-026","IF-032","IF-037"] },
  T4S:      { x: COL[1], y: laneCenterY("L3_MES"),     role: "gateway",     color: T.cyan,   label: "T4S",             sub: "TC ↔ SAP",            version: "2024",   level: "L3_MES",     icon: "🔄", standard: "Siemens T4S",
              businessObjects: ["MaterialMaster","BOM","STKO","STPO","Routing","DocInfoRecord"],
              interfaces: ["IF-013","IF-026","IF-032"] },
  MES:      { x: COL[2], y: laneCenterY("L3_MES"),     role: "hub",         color: T.blue,   label: "Opcenter EX",     sub: "MES",                 version: "2507",   level: "L3_MES",     icon: "⚙️", standard: "ISA-95 L3",
              businessObjects: ["Spec","WorkflowStep","BOMItem","WorkOrder","Container","StepExecution","DataCollection","NonConformance","Resource","Tool","MaintenanceOrder","Document","Genealogy"],
              interfaces: ["IF-001","IF-002","IF-003","IF-004","IF-005","IF-006","IF-007","IF-008","IF-009","IF-010","IF-011","IF-018","IF-021","IF-022","IF-023","IF-025","IF-027","IF-031","IF-034","IF-035","IF-036","IF-039","IF-040"] },
  CN4T:     { x: COL[3], y: laneCenterY("L3_MES"),     role: "gateway",     color: T.cyan,   label: "CN4T",            sub: "TC → Opcenter",       version: "2510",   level: "L3_MES",     icon: "🔄", standard: "Siemens CN4T",
              businessObjects: ["BOPOperation","WorkflowStep","MBOM","BOMItem","WorkPackage","Spec","NCDataset","Document","BOEEquipment","Resource","Tooling","MaintenancePlan"],
              interfaces: ["IF-001","IF-002","IF-003","IF-004","IF-005","IF-006","IF-035","IF-040"] },
  CN4S:     { x: COL[4], y: laneCenterY("L3_MES"),     role: "gateway",     color: T.amber,  label: "CN4S",            sub: "SAP ↔ Opcenter",      version: "2510",   level: "L3_MES",     icon: "🔄", standard: "Siemens CN4S",
              businessObjects: ["ProductionOrder","WorkOrder","GoodsIssue","Inventory","Confirmation","CO","QMResult","InspectionLot","NCR","QualityNotification","MaterialMaster","CycleCount"],
              interfaces: ["IF-007","IF-008","IF-009","IF-010","IF-011","IF-029","IF-034"] },
  APS:      { x: COL[5], y: laneCenterY("L3_MES"),     role: "application", color: T.amber,  label: "Opcenter APS",    sub: "Planning · scheduling",version:"2510",   level: "L3_MES",     icon: "📋", standard: "ISA-95 L3",
              businessObjects: ["Schedule","CapacityPlan","DispatchList","BottleneckAnalysis"],
              interfaces: ["IF-016"] },
  EASYPLAN: { x: COL[6], y: laneCenterY("L3_MES"),     role: "application", color: T.gold,   label: "EasyPlan",        sub: "Process engineering", version:"2024",   level: "L3_MES",     icon: "📐", standard: "ISA-95 L3",
              businessObjects: ["BOP","BalancedBOP","MTMTime","StationAssignment","TaktTime","CapacityPlan","OEE","Ergonomics","REBA"],
              interfaces: ["IF-015","IF-016","IF-017"] },

  // L2 · Process control
  SFC:      { x: COL[0], y: laneCenterY("L2_CONTROL"), role: "application", color: T.orange, label: "SFC terminals",   sub: "Shop floor UI",       version: "2.0",    level: "L2_CONTROL", icon: "📱", standard: "ISA-95 L2",
              businessObjects: ["WorkInstruction","StepConfirmation","QCData","TimeEntry","DataCollection"],
              interfaces: ["IF-018","IF-019"] },
  EDGE:     { x: COL[2], y: laneCenterY("L2_CONTROL"), role: "application", color: T.teal,   label: "Industrial Edge", sub: "OPC-UA · IEM",        version: "2.0",    level: "L2_CONTROL", icon: "🔌", standard: "OPC-UA 1.05",
              businessObjects: ["ProcessSignal","Alarm","Trend","TagValue","HistoricData"],
              interfaces: ["IF-019","IF-021","IF-022","IF-038"] },
  QUAL:     { x: COL[4], y: laneCenterY("L2_CONTROL"), role: "application", color: T.pink,   label: "Quality lab",     sub: "QM · SPC · NCR",      version: "1.0",    level: "L2_CONTROL", icon: "🔬", standard: "ISA-95 L2",
              businessObjects: ["InspectionResult","NCR","CalibrationCert","SPCData","TestSpec"],
              interfaces: ["IF-010","IF-011","IF-025","IF-031","IF-039"] },

  // L1 · Sensors / devices
  PLC:      { x: COL[0], y: laneCenterY("L1_SENSORS"), role: "device",      color: T.steel,  label: "PLCs",            sub: "S7-1500 · others",                       level: "L1_SENSORS", icon: "🤖", standard: "IEC 61131",
              businessObjects: ["IOPoint","TagDB","ProgramBlock","DiagBuffer","FaultCode"],
              interfaces: ["IF-019"] },
  CNC:      { x: COL[1], y: laneCenterY("L1_SENSORS"), role: "device",      color: T.steel,  label: "CNC machines",    sub: "Sinumerik · Fanuc",                      level: "L1_SENSORS", icon: "⚙️", standard: "ISO 6983",
              businessObjects: ["NCProgram","SpindleSpeed","FeedRate","SpindleLoad","ActiveProgram","AlarmList","ToolData","AxisPosition"],
              interfaces: ["IF-019","IF-020","IF-021"] },
  ROBOT:    { x: COL[3], y: laneCenterY("L1_SENSORS"), role: "device",      color: T.purple, label: "Robotics",        sub: "6-axis · cobot · AGV",                   level: "L1_SENSORS", icon: "🤖", standard: "ISO 10218",
              businessObjects: ["RobotProgram","AxisPosition","Speed","Payload","CycleCount","MaintenanceCounter","GripperStatus"],
              interfaces: ["IF-019"] },
  SENSORS:  { x: COL[4], y: laneCenterY("L1_SENSORS"), role: "device",      color: T.teal,   label: "IIoT sensors",    sub: "Vib · temp · pressure",                  level: "L1_SENSORS", icon: "🌡️", standard: "OPC-UA / MQTT",
              businessObjects: ["Temperature","Humidity","Vibration","ParticleCount","Pressure","Current","Power","FlowRate"],
              interfaces: ["IF-038"] },

  // L0 · Physical
  CONVEYOR: { x: COL[1], y: laneCenterY("L0_PHYSICAL"),role: "physical",    color: T.steel,  label: "Material handling", sub: "Conveyors · AGVs",                     level: "L0_PHYSICAL",icon: "📦",
              businessObjects: ["MaterialPosition","Speed","JamStatus","Throughput","QueueLength"], interfaces: [] },
  PROCESS:  { x: COL[3], y: laneCenterY("L0_PHYSICAL"),role: "physical",    color: T.steel,  label: "Production process",sub: "Forming · joining · assy",             level: "L0_PHYSICAL",icon: "🏭",
              businessObjects: ["WorkInProgress","CycleTime","DefectCount","Yield","OEE"], interfaces: [] },
};

// Helper: get node size honoring role + per-node override
const getNodeSize = (node) => ({
  w: node.w || NODE_SIZE[node.role].w,
  h: node.h || NODE_SIZE[node.role].h,
});

// ═══════════════════════════════════════════════════════════════════════════
// 4 · CONNECTION CATALOG · flow types · semantics
// ═══════════════════════════════════════════════════════════════════════════
const FLOW_TYPES = {
  sequence:    { label: "Sequence flow",    dash: "none",  desc: "Synchronous request / response" },
  message:     { label: "Message flow",     dash: "8,4",   desc: "Asynchronous event over bus / queue" },
  data:        { label: "Data flow",        dash: "4,4",   desc: "Bulk data, periodic sync or replication" },
  association: { label: "Association",      dash: "2,2",   desc: "Logical link, not a runtime flow" },
};

const FLOW_CATEGORY_META = {
  "PLM-TO-MES":         { label: "PLM → MES",          color: T.cyan },
  "MES-TO-ERP":         { label: "MES ↔ ERP",          color: T.amber },
  "PLM-TO-ERP":         { label: "PLM ↔ ERP",          color: T.blue },
  "PROCESS-ENGINEERING":{ label: "Process engineering",color: T.gold },
  "CLOUD-ANALYTICS":    { label: "Cloud analytics",    color: T.purple },
  "MES-SHOP-FLOOR":     { label: "MES ↔ shop floor",   color: T.orange },
  "EDGE-INTEGRATION":   { label: "Edge integration",   color: T.teal },
  "QUALITY-LAB":        { label: "Quality lab",        color: T.pink },
  "ESB-FANOUT":         { label: "ESB fan-out",        color: T.green },
};

const CONNECTIONS = [
  // PLM ↔ ESB ↔ rest of the world (the modern integration story)
  { from: "PLM",  to: "ESB",      interfaces: ["IF-001","IF-002","IF-003","IF-035","IF-040"], flowType: "message",  category: "ESB-FANOUT" },
  { from: "ESB",  to: "MES",      interfaces: ["IF-001","IF-002","IF-003","IF-035","IF-040"], flowType: "sequence", category: "ESB-FANOUT" },
  { from: "ESB",  to: "ERP",      interfaces: ["IF-013","IF-026","IF-032"],                   flowType: "sequence", category: "ESB-FANOUT" },
  { from: "ESB",  to: "HUB",      interfaces: ["IF-024","IF-028"],                            flowType: "data",     category: "ESB-FANOUT" },

  // PLM ↔ MES (legacy direct, kept for completeness — shown in implementer view)
  { from: "PLM",  to: "CN4T",     interfaces: ["IF-001","IF-002","IF-003","IF-004","IF-005","IF-006","IF-035","IF-040"], flowType: "message",  category: "PLM-TO-MES" },
  { from: "CN4T", to: "MES",      interfaces: ["IF-001","IF-002","IF-003","IF-004","IF-005","IF-006","IF-035","IF-040"], flowType: "sequence", category: "PLM-TO-MES" },

  // MES ↔ ERP
  { from: "MES",  to: "CN4S",     interfaces: ["IF-007","IF-008","IF-009","IF-010","IF-011","IF-029","IF-034"], flowType: "message",  category: "MES-TO-ERP" },
  { from: "CN4S", to: "ERP",      interfaces: ["IF-007","IF-008","IF-009","IF-010","IF-011","IF-029","IF-034"], flowType: "sequence", category: "MES-TO-ERP" },

  // PLM ↔ ERP via AIG + T4S
  { from: "PLM",  to: "AIG",      interfaces: ["IF-012","IF-013","IF-026","IF-032","IF-037"], flowType: "message",  category: "PLM-TO-ERP" },
  { from: "AIG",  to: "ERP",      interfaces: ["IF-012","IF-014","IF-037"],                    flowType: "sequence", category: "PLM-TO-ERP" },
  { from: "PLM",  to: "T4S",      interfaces: ["IF-013","IF-026","IF-032"],                    flowType: "message",  category: "PLM-TO-ERP" },
  { from: "T4S",  to: "ERP",      interfaces: ["IF-013","IF-026","IF-032"],                    flowType: "sequence", category: "PLM-TO-ERP" },

  // Planning & process eng
  { from: "EASYPLAN", to: "PLM",  interfaces: ["IF-015"], flowType: "data", category: "PROCESS-ENGINEERING" },
  { from: "EASYPLAN", to: "ERP",  interfaces: ["IF-016"], flowType: "data", category: "PROCESS-ENGINEERING" },
  { from: "EASYPLAN", to: "HUB",  interfaces: ["IF-017"], flowType: "data", category: "CLOUD-ANALYTICS" },
  { from: "APS",      to: "MES",  interfaces: ["IF-016"], flowType: "sequence", category: "PROCESS-ENGINEERING" },

  // B2B
  { from: "ERP",      to: "SUPPLIER", interfaces: ["IF-014"], flowType: "message", category: "PLM-TO-ERP" },
  { from: "ERP",      to: "CUSTOMER", interfaces: [],          flowType: "message", category: "PLM-TO-ERP" },

  // MES ↔ shop floor
  { from: "MES",  to: "SFC",  interfaces: ["IF-018"], flowType: "sequence", category: "MES-SHOP-FLOOR" },
  { from: "MES",  to: "EDGE", interfaces: ["IF-021","IF-022"], flowType: "data", category: "EDGE-INTEGRATION" },
  { from: "MES",  to: "QUAL", interfaces: ["IF-010","IF-011","IF-025","IF-031","IF-039"], flowType: "data", category: "QUALITY-LAB" },

  // L2 ↔ L1
  { from: "SFC",  to: "CNC",  interfaces: ["IF-019","IF-020"], flowType: "sequence", category: "MES-SHOP-FLOOR" },
  { from: "SFC",  to: "PLC",  interfaces: ["IF-019"],          flowType: "sequence", category: "MES-SHOP-FLOOR" },
  { from: "EDGE", to: "CNC",  interfaces: ["IF-019","IF-020","IF-021"], flowType: "data", category: "EDGE-INTEGRATION" },
  { from: "EDGE", to: "PLC",  interfaces: ["IF-019"], flowType: "data", category: "EDGE-INTEGRATION" },
  { from: "EDGE", to: "ROBOT",interfaces: ["IF-019"], flowType: "data", category: "EDGE-INTEGRATION" },
  { from: "EDGE", to: "SENSORS", interfaces: ["IF-038"], flowType: "data", category: "EDGE-INTEGRATION" },

  // Quality ↔ MES
  { from: "QUAL", to: "MES",  interfaces: ["IF-010","IF-011","IF-025","IF-031","IF-039"], flowType: "message", category: "QUALITY-LAB" },

  // Cloud ↔ everything
  { from: "HUB",  to: "MES",  interfaces: ["IF-022","IF-023","IF-036"], flowType: "data", category: "CLOUD-ANALYTICS" },
  { from: "HUB",  to: "PLM",  interfaces: ["IF-024","IF-028"], flowType: "data", category: "CLOUD-ANALYTICS" },
  { from: "HUB",  to: "ERP",  interfaces: ["IF-023"], flowType: "data", category: "CLOUD-ANALYTICS" },

  // L1 → L0
  { from: "PLC",      to: "CONVEYOR", interfaces: [], flowType: "association", category: "EDGE-INTEGRATION" },
  { from: "CNC",      to: "PROCESS",  interfaces: [], flowType: "association", category: "EDGE-INTEGRATION" },
  { from: "ROBOT",    to: "PROCESS",  interfaces: [], flowType: "association", category: "EDGE-INTEGRATION" },
  { from: "SENSORS",  to: "PROCESS",  interfaces: [], flowType: "association", category: "EDGE-INTEGRATION" },
];

// All unique business objects (for the BO Lens)
const ALL_BUSINESS_OBJECTS = (() => {
  const set = new Set();
  for (const sys of Object.values(NODES)) (sys.businessObjects || []).forEach(b => set.add(b));
  return [...set].sort();
})();

// ═══════════════════════════════════════════════════════════════════════════
// 5 · VIEW PRESETS
// ═══════════════════════════════════════════════════════════════════════════
const VIEW_PRESETS = {
  executive: {
    label: "🎩 Executive view",
    desc: "High-level — L4 + L3 only, no devices, no animation",
    visibleLevels: { L4_ERP: true, L3_MES: true, L2_CONTROL: false, L1_SENSORS: false, L0_PHYSICAL: false },
    showAnimated: false, showAssociations: false, hideLegacyDirect: true,
  },
  implementer: {
    label: "🛠 Implementer view",
    desc: "Everything visible · animated · legacy direct flows shown",
    visibleLevels: { L4_ERP: true, L3_MES: true, L2_CONTROL: true, L1_SENSORS: true, L0_PHYSICAL: true },
    showAnimated: true,  showAssociations: true,  hideLegacyDirect: false,
  },
  operator: {
    label: "👷 Operator view",
    desc: "L3–L0 shop-floor focus, no enterprise",
    visibleLevels: { L4_ERP: false, L3_MES: true, L2_CONTROL: true, L1_SENSORS: true, L0_PHYSICAL: true },
    showAnimated: true,  showAssociations: false, hideLegacyDirect: false,
  },
  print: {
    label: "🖨 Print / export",
    desc: "Light background, no animation, hi-contrast",
    visibleLevels: { L4_ERP: true, L3_MES: true, L2_CONTROL: true, L1_SENSORS: true, L0_PHYSICAL: true },
    showAnimated: false, showAssociations: true,  hideLegacyDirect: false, printMode: true,
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// 6 · NODE SHAPE RENDERERS
// ═══════════════════════════════════════════════════════════════════════════
function NodeShape({ node, w, h, fill, stroke, strokeWidth, opacity = 1 }) {
  const hw = w / 2, hh = h / 2;

  switch (node.role) {
    case "hub":
    case "application":
    case "external":
      // Rounded rectangle (external uses dashed border, applied by caller)
      return (
        <rect x={-hw} y={-hh} width={w} height={h}
          rx={node.role === "hub" ? 12 : 8}
          ry={node.role === "hub" ? 12 : 8}
          fill={fill} stroke={stroke} strokeWidth={strokeWidth} opacity={opacity}
          strokeDasharray={node.role === "external" ? "6,3" : "none"}
        />
      );

    case "gateway": {
      // Hexagon — gateways are the "filters" of the integration story
      const insetX = hh * 0.45;
      const pts = [
        `${-hw + insetX},${-hh}`,
        `${hw - insetX},${-hh}`,
        `${hw},0`,
        `${hw - insetX},${hh}`,
        `${-hw + insetX},${hh}`,
        `${-hw},0`,
      ].join(" ");
      return <polygon points={pts} fill={fill} stroke={stroke} strokeWidth={strokeWidth} opacity={opacity}/>;
    }

    case "bus": {
      // Wide horizontal capsule (rounded rect with extreme rounding)
      return (
        <rect x={-hw} y={-hh} width={w} height={h}
          rx={hh} ry={hh}
          fill={fill} stroke={stroke} strokeWidth={strokeWidth} opacity={opacity}
        />
      );
    }

    case "database": {
      // Cylinder — open rectangle + top/bottom ellipses
      const ry = h * 0.15;
      return (
        <g opacity={opacity}>
          <path d={`M ${-hw} ${-hh + ry} L ${-hw} ${hh - ry} A ${hw} ${ry} 0 0 0 ${hw} ${hh - ry} L ${hw} ${-hh + ry}`}
                fill={fill} stroke={stroke} strokeWidth={strokeWidth}/>
          <ellipse cx={0} cy={-hh + ry} rx={hw} ry={ry}
                fill={fill} stroke={stroke} strokeWidth={strokeWidth}/>
          <ellipse cx={0} cy={hh - ry} rx={hw} ry={ry}
                fill="none" stroke={stroke} strokeWidth={strokeWidth} opacity={0.4}/>
        </g>
      );
    }

    case "physical": {
      // Trapezoid — physical assets
      const taper = hw * 0.18;
      const pts = [
        `${-hw + taper},${-hh}`,
        `${hw - taper},${-hh}`,
        `${hw},${hh}`,
        `${-hw},${hh}`,
      ].join(" ");
      return <polygon points={pts} fill={fill} stroke={stroke} strokeWidth={strokeWidth} opacity={opacity}/>;
    }

    case "device": {
      // Octagon — devices live at the edge of digital ↔ physical
      const inset = Math.min(w, h) * 0.22;
      const pts = [
        `${-hw + inset},${-hh}`,
        `${hw - inset},${-hh}`,
        `${hw},${-hh + inset}`,
        `${hw},${hh - inset}`,
        `${hw - inset},${hh}`,
        `${-hw + inset},${hh}`,
        `${-hw},${hh - inset}`,
        `${-hw},${-hh + inset}`,
      ].join(" ");
      return <polygon points={pts} fill={fill} stroke={stroke} strokeWidth={strokeWidth} opacity={opacity}/>;
    }

    default:
      return (
        <rect x={-hw} y={-hh} width={w} height={h} rx={6}
          fill={fill} stroke={stroke} strokeWidth={strokeWidth} opacity={opacity}/>
      );
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// 7 · MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
export default function VisualSelection({
  state, dispatch,
  onInterfaceSelect, selectedInterfaceId, setSelectedInterface,
}) {
  // ─── State ──────────────────────────────────────────────────────────
  const [hoveredNode, setHoveredNode] = useState(null);
  const [hoveredConnection, setHoveredConnection] = useState(null);
  const [selectedNodes, setSelectedNodes] = useState(new Set());
  const [selectedInterfacesL, setSelectedInterfacesL] = useState(new Set());
  const [focusNode, setFocusNode] = useState(null);
  const [boLens, setBoLens] = useState("");

  const [viewPreset, setViewPreset] = useState("implementer");
  const preset = VIEW_PRESETS[viewPreset];
  const [visibleLevels, setVisibleLevels] = useState(preset.visibleLevels);
  const [showAnimated, setShowAnimated] = useState(preset.showAnimated);
  const [showAssociations, setShowAssociations] = useState(preset.showAssociations);
  const [hideLegacyDirect, setHideLegacyDirect] = useState(preset.hideLegacyDirect);
  const [showLegend, setShowLegend] = useState(true);
  const [printMode, setPrintMode] = useState(false);

  // Sync preset → preset-controlled state
  useEffect(() => {
    setVisibleLevels(preset.visibleLevels);
    setShowAnimated(preset.showAnimated);
    setShowAssociations(preset.showAssociations);
    setHideLegacyDirect(preset.hideLegacyDirect);
    setPrintMode(!!preset.printMode);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewPreset]);

  // Pan / zoom
  const [zoom, setZoom] = useState(0.62);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const svgRef = useRef(null);
  const containerRef = useRef(null);

  const nav = useNavigation();

  // ─── Filter wiring ──────────────────────────────────────────────────
  const filteredInterfaces = useFilteredInterfaces(state);
  const filteredIds = useMemo(
    () => new Set(filteredInterfaces.map(f => f.id)),
    [filteredInterfaces]
  );

  // ─── Per-interface state lookup ─────────────────────────────────────
  const getInterfaceState = useCallback((ifaceId) => {
    const iface = IFACES.find(f => f.id === ifaceId);
    if (!iface) return null;
    const progress = getInterfaceProgress(iface, state.dodState, state.nisState);
    const isNIS = !!state.nisState?.[ifaceId];
    return {
      id: ifaceId, name: iface.name, from: iface.from, to: iface.to,
      grp: iface.grp, mvp: iface.mvp, phase: iface.phase, risk: iface.risk,
      effort: iface.effort, progress: progress.pct, isNIS,
      hasComments: (state.comments?.[ifaceId]?.length || 0) > 0,
      protocolStandard: getProtocolsByInterface(ifaceId)?.primaryProtocol,
      triggerCount: iface.triggers?.length || 0,
      taskCount: iface.tasks?.length || 0,
      businessObject: iface.bo,
      color: GRP_META[iface.grp]?.color || T.steel,
    };
  }, [state.dodState, state.nisState, state.comments]);

  const getNodeState = useCallback((nodeKey) => {
    const node = NODES[nodeKey];
    if (!node) return null;
    const ids = [...new Set(
      CONNECTIONS.filter(c => c.from === nodeKey || c.to === nodeKey).flatMap(c => c.interfaces)
    )];
    const ifaceStates = ids.map(getInterfaceState).filter(Boolean);
    const totalProgress = ifaceStates.length === 0 ? 0
      : Math.round(ifaceStates.reduce((s, i) => s + i.progress, 0) / ifaceStates.length);
    return {
      totalIfaces: ids.length, totalProgress,
      completedCount: ifaceStates.filter(i => i.progress === 100).length,
      inProgressCount: ifaceStates.filter(i => i.progress > 0 && i.progress < 100).length,
      nisCount: ifaceStates.filter(i => i.isNIS).length,
      highRiskCount: ifaceStates.filter(i => i.risk >= 3 && !i.isNIS).length,
      ifaceStates,
    };
  }, [getInterfaceState]);

  // ─── Focus mode & BO lens ──────────────────────────────────────────
  const focusNeighbors = useMemo(() => {
    if (!focusNode) return null;
    const set = new Set([focusNode]);
    for (const c of CONNECTIONS) {
      if (c.from === focusNode) set.add(c.to);
      if (c.to === focusNode) set.add(c.from);
    }
    return set;
  }, [focusNode]);

  const isNodeFocused = useCallback((k) => !focusNode || focusNeighbors?.has(k), [focusNode, focusNeighbors]);
  const isConnectionFocused = useCallback((c) => !focusNode || c.from === focusNode || c.to === focusNode, [focusNode]);

  const boMatchingNodes = useMemo(() => {
    if (!boLens) return null;
    const set = new Set();
    for (const [key, sys] of Object.entries(NODES)) {
      if ((sys.businessObjects || []).some(b => b.toLowerCase().includes(boLens.toLowerCase()))) set.add(key);
    }
    return set;
  }, [boLens]);

  const isConnectionBoMatch = useCallback((c) => {
    if (!boLens) return true;
    if (boMatchingNodes?.has(c.from) || boMatchingNodes?.has(c.to)) return true;
    return c.interfaces.some(id => {
      const iface = IFACES.find(f => f.id === id);
      return iface?.bo?.toLowerCase().includes(boLens.toLowerCase());
    });
  }, [boLens, boMatchingNodes]);

  const isNodeBoMatch = useCallback((k) => !boLens || boMatchingNodes?.has(k), [boLens, boMatchingNodes]);

  // ─── Edge geometry ──────────────────────────────────────────────────
  const edgeData = useMemo(() => {
    return CONNECTIONS.map(conn => {
      // Hide legacy direct PLM→CN4T or PLM→MES when ESB-only mode
      if (hideLegacyDirect &&
          (conn.category === "PLM-TO-MES" || conn.category === "MES-TO-ERP" || conn.category === "PLM-TO-ERP") &&
          conn.from !== "ESB" && conn.to !== "ESB") return null;
      // Hide associations toggle
      if (!showAssociations && conn.flowType === "association") return null;

      const fromNode = NODES[conn.from];
      const toNode   = NODES[conn.to];
      if (!fromNode || !toNode) return null;
      if (!visibleLevels[fromNode.level] && !visibleLevels[toNode.level]) return null;

      const interfacesPassingFilter = conn.interfaces.filter(id => filteredIds.has(id));
      let avgPct = 0, count = 0;
      for (const id of interfacesPassingFilter) {
        const s = getInterfaceState(id);
        if (s && !s.isNIS) { avgPct += s.progress; count++; }
      }
      avgPct = count > 0 ? avgPct / count : 0;

      // Color: per-category, but tinted by progress
      const catColor = FLOW_CATEGORY_META[conn.category]?.color || T.steel;
      let color = catColor;
      if (avgPct === 100) color = T.green;
      else if (avgPct >= 75) color = catColor;
      else if (avgPct >= 50) color = T.amber;
      else if (avgPct > 0) color = T.orange;
      else if (interfacesPassingFilter.length === 0 && conn.interfaces.length > 0) color = T.text3;

      const isHovered = hoveredConnection === `${conn.from}-${conn.to}`;
      const isSelected = selectedInterfacesL.size > 0 && conn.interfaces.some(id => selectedInterfacesL.has(id));
      const isActive = interfacesPassingFilter.length > 0;
      const focused = isConnectionFocused(conn);
      const boMatch = isConnectionBoMatch(conn);

      const baseWidth = 1 + Math.log2(interfacesPassingFilter.length + 1) * 1.8;
      const mul = isHovered ? 1.7 : isSelected ? 1.5 : isActive ? 1 : 0.5;
      const strokeWidth = Math.max(0.6, Math.min(8, baseWidth * mul));

      let opacity = isActive ? 1 : 0.3;
      if (!focused) opacity *= 0.18;
      if (!boMatch) opacity *= 0.18;
      if (isHovered) opacity = Math.max(opacity, 0.95);

      // Edge geometry — anchor on node EDGES (not centres) so lines never
      // cut through node bodies, with smooth vertical-biased S-curves.
      const fSize = getNodeSize(fromNode), tSize = getNodeSize(toNode);
      const sameLane = fromNode.level === toNode.level && fromNode.role !== "bus" && toNode.role !== "bus";

      let sx, sy, ex, ey, pathData;
      if (sameLane) {
        // Same lane: exit/enter from top edges, arc gently above the row
        sx = fromNode.x; sy = fromNode.y - fSize.h / 2;
        ex = toNode.x;   ey = toNode.y - tSize.h / 2;
        const dx = ex - sx;
        const lift = 26 + Math.abs(dx) * 0.10;
        pathData = `M ${sx} ${sy} C ${sx + dx * 0.25} ${sy - lift}, ${ex - dx * 0.25} ${ey - lift}, ${ex} ${ey}`;
      } else {
        // Cross-lane: exit bottom of upper node, enter top of lower node
        const goingDown = toNode.y > fromNode.y;
        sx = fromNode.x; sy = fromNode.y + (goingDown ? fSize.h / 2 : -fSize.h / 2);
        ex = toNode.x;   ey = toNode.y + (goingDown ? -tSize.h / 2 : tSize.h / 2);
        const dy = ey - sy;
        // Strong vertical bias → clean, parallel-looking trunk lines
        pathData = `M ${sx} ${sy} C ${sx} ${sy + dy * 0.45}, ${ex} ${ey - dy * 0.45}, ${ex} ${ey}`;
      }

      return {
        ...conn, fromNode, toNode,
        flowColor: color, dashArray: FLOW_TYPES[conn.flowType]?.dash || "none",
        interfacesPassingFilter, isHovered, isSelected, isActive, focused, boMatch,
        strokeWidth, opacity, pathData, key: `${conn.from}-${conn.to}-${conn.category}`,
      };
    }).filter(Boolean);
  }, [visibleLevels, filteredIds, getInterfaceState, hoveredConnection,
      selectedInterfacesL, isConnectionFocused, isConnectionBoMatch,
      hideLegacyDirect, showAssociations]);

  // ─── Node interaction ──────────────────────────────────────────────
  const handleNodeClick = useCallback((nodeKey, event) => {
    event.stopPropagation();
    if (event.altKey) {
      setFocusNode(prev => prev === nodeKey ? null : nodeKey);
      return;
    }
    const ids = [...new Set(
      CONNECTIONS.filter(c => c.from === nodeKey || c.to === nodeKey).flatMap(c => c.interfaces)
    )];
    if (event.ctrlKey || event.metaKey) {
      setSelectedNodes(prev => {
        const ns = new Set(prev);
        if (ns.has(nodeKey)) ns.delete(nodeKey); else ns.add(nodeKey);
        return ns;
      });
      setSelectedInterfacesL(prev => {
        const ns = new Set(prev);
        ids.forEach(id => { if (ns.has(id)) ns.delete(id); else ns.add(id); });
        return ns;
      });
    } else if (event.shiftKey) {
      setSelectedInterfacesL(prev => new Set([...prev, ...ids]));
      setSelectedNodes(prev => new Set([...prev, nodeKey]));
    } else {
      setSelectedNodes(new Set([nodeKey]));
      setSelectedInterfacesL(new Set(ids));
    }
  }, []);

  const handleInterfaceClick = useCallback((ifaceId, event) => {
    if (event.ctrlKey || event.metaKey) {
      setSelectedInterfacesL(prev => {
        const ns = new Set(prev);
        if (ns.has(ifaceId)) ns.delete(ifaceId); else ns.add(ifaceId);
        return ns;
      });
    } else {
      setSelectedInterfacesL(new Set([ifaceId]));
      onInterfaceSelect && onInterfaceSelect(ifaceId);
    }
  }, [onInterfaceSelect]);

  // Pan / zoom
  const onMouseDown = (e) => {
    if (e.target.closest("[data-node]") || e.target.closest("[data-interaction]")) return;
    setIsDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY, panX: pan.x, panY: pan.y };
  };
  const onMouseMove = (e) => {
    if (!isDragging) return;
    setPan({ x: dragStartRef.current.panX + (e.clientX - dragStartRef.current.x),
             y: dragStartRef.current.panY + (e.clientY - dragStartRef.current.y) });
  };
  const onMouseUp = () => setIsDragging(false);
  const onWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.93 : 1.07;
    setZoom(z => Math.max(0.25, Math.min(3, z * delta)));
  };
  const resetView = () => { setZoom(0.62); setPan({ x: 0, y: 0 }); };

  const onCanvasClick = (e) => {
    if (e.target === e.currentTarget || e.target.tagName === "svg") {
      setSelectedNodes(new Set());
      setSelectedInterfacesL(new Set());
    }
  };

  // ─── Selected interfaces panel data ─────────────────────────────────
  const selectedDetailList = useMemo(() => {
    return [...selectedInterfacesL]
      .map(getInterfaceState)
      .filter(Boolean)
      .sort((a, b) => a.id.localeCompare(b.id));
  }, [selectedInterfacesL, getInterfaceState]);

  // Background colors driven by printMode
  const BG       = printMode ? "#ffffff" : T.bg;
  const PANEL_BG = printMode ? "#f4f6fa" : T.bg2;
  const PANEL_BORDER = printMode ? "#cbd5e1" : T.border;
  const TEXT     = printMode ? "#0f172a" : T.text;
  const TEXT2    = printMode ? "#475569" : T.text2;
  const TEXT3    = printMode ? "#64748b" : T.text3;

  // ─── Render ────────────────────────────────────────────────────────
  return (
    <div style={{ display: "flex", height: "calc(100vh - 108px)", overflow: "hidden", background: BG }}>
      {/* ─── Left side: filter panel (existing) ─── */}
      <FilterPanel state={state} dispatch={dispatch} />

      {/* ─── Center: canvas + toolbars ─── */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        {/* Top toolbar */}
        <Toolbar
          viewPreset={viewPreset} setViewPreset={setViewPreset}
          visibleLevels={visibleLevels} setVisibleLevels={setVisibleLevels}
          showAnimated={showAnimated} setShowAnimated={setShowAnimated}
          showAssociations={showAssociations} setShowAssociations={setShowAssociations}
          hideLegacyDirect={hideLegacyDirect} setHideLegacyDirect={setHideLegacyDirect}
          showLegend={showLegend} setShowLegend={setShowLegend}
          printMode={printMode}
          boLens={boLens} setBoLens={setBoLens}
          focusNode={focusNode} setFocusNode={setFocusNode}
          zoom={zoom} setZoom={setZoom} resetView={resetView}
          counts={{ ifaces: filteredInterfaces.length, total: IFACES.length, selected: selectedInterfacesL.size }}
        />

        {/* Canvas region */}
        <div ref={containerRef} style={{
          flex: 1, position: "relative", overflow: "hidden",
          background: BG, cursor: isDragging ? "grabbing" : "grab",
        }}>
          <svg ref={svgRef}
            width="100%" height="100%"
            viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`}
            preserveAspectRatio="xMidYMid meet"
            onMouseDown={onMouseDown}
            onMouseMove={onMouseMove}
            onMouseUp={onMouseUp}
            onMouseLeave={onMouseUp}
            onWheel={onWheel}
            onClick={onCanvasClick}
            style={{ background: BG, userSelect: "none" }}
          >
            <defs>
              {/* Arrow marker per category color */}
              {Object.entries(FLOW_CATEGORY_META).map(([k, m]) => (
                <marker key={k} id={`arr-${k}`} viewBox="0 0 10 10" refX="9" refY="5"
                  markerWidth="6" markerHeight="6" orient="auto">
                  <path d="M0,0 L10,5 L0,10 z" fill={m.color}/>
                </marker>
              ))}
              {/* Glow filter for active nodes */}
              <filter id="vs-glow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="3" result="b"/>
                <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
              </filter>
              {/* Per-lane gradient backgrounds */}
              {ISA95_LANES.map(l => (
                <linearGradient key={l.id} id={`lane-${l.id}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={l.color} stopOpacity={printMode ? 0.05 : 0.04}/>
                  <stop offset="100%" stopColor={l.color} stopOpacity={printMode ? 0.02 : 0.01}/>
                </linearGradient>
              ))}
            </defs>

            <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>

              {/* ─── Left label gutter (vertical panel; nodes never enter) ─── */}
              <rect x={0} y={0} width={LABEL_W} height={CANVAS_H}
                fill={printMode ? "#eef2f8" : "#040d18"} opacity={printMode ? 1 : 0.6}/>
              <line x1={LABEL_W} y1={0} x2={LABEL_W} y2={CANVAS_H}
                stroke={PANEL_BORDER} strokeWidth="1"/>

              {/* ─── Swim-lane backgrounds (start after the gutter) ─── */}
              {ISA95_LANES.filter(l => visibleLevels[l.id]).map(lane => (
                <g key={lane.id}>
                  <rect x={LABEL_W} y={lane.y} width={CANVAS_W - LABEL_W} height={lane.h}
                    fill={`url(#lane-${lane.id})`}
                    stroke={`${lane.color}25`} strokeWidth="1"
                  />
                  {/* Coloured spine on the lane's left edge */}
                  <rect x={LABEL_W} y={lane.y} width={4} height={lane.h} fill={lane.color} opacity={0.55}/>

                  {/* Lane label block — lives entirely inside the gutter */}
                  <g transform={`translate(18, ${lane.y + lane.h / 2})`}>
                    {/* big level key */}
                    <text x={0} y={-14} fontSize="26" fontWeight="800" fill={lane.color}
                      fontFamily={FONTS.header} opacity={0.95}>{lane.level}</text>
                    <text x={0} y={6} fontSize="10.5" fontWeight="700" fill={TEXT}
                      letterSpacing="0.5" fontFamily={FONTS.mono}>
                      {lane.icon} {lane.label}
                    </text>
                    {/* subtitle wrapped to gutter width */}
                    <text x={0} y={22} fontSize="8.5" fill={TEXT2} fontStyle="italic">
                      {lane.subtitle}
                    </text>
                  </g>
                </g>
              ))}

              {/* ─── Dedicated ESB band (between L4 and L3, its own strip) ─── */}
              {visibleLevels.L3_MES && (
                <g>
                  <rect x={LABEL_W} y={BUS_Y0} width={CANVAS_W - LABEL_W} height={BUS_BAND_H}
                    fill={`${T.green}0c`} stroke={`${T.green}30`} strokeWidth="1" strokeDasharray="2,4"/>
                  <rect x={LABEL_W} y={BUS_Y0} width={4} height={BUS_BAND_H} fill={T.green} opacity={0.55}/>
                  <g transform={`translate(18, ${BUS_Y0 + BUS_BAND_H / 2})`}>
                    <text x={0} y={-4} fontSize="13" fontWeight="800" fill={T.green} fontFamily={FONTS.header}>ESB</text>
                    <text x={0} y={12} fontSize="7.5" fill={TEXT2} fontFamily={FONTS.mono}>message bus</text>
                  </g>
                </g>
              )}

              {/* ─── Connection edges ─── */}
              {edgeData.map(e => (
                <ConnectionPath key={e.key} edge={e}
                  showAnimated={showAnimated}
                  onHover={setHoveredConnection}
                  onInterfaceClick={handleInterfaceClick}
                  filteredIds={filteredIds}
                  getInterfaceState={getInterfaceState}
                />
              ))}

              {/* ─── Nodes ─── */}
              {Object.entries(NODES).filter(([key, n]) => visibleLevels[n.level]).map(([key, node]) => (
                <NodeRender key={key}
                  nodeKey={key} node={node}
                  state={getNodeState(key)}
                  hovered={hoveredNode === key}
                  selected={selectedNodes.has(key)}
                  focused={isNodeFocused(key)}
                  boMatch={isNodeBoMatch(key)}
                  isFocusedRoot={focusNode === key}
                  onHover={setHoveredNode}
                  onClick={handleNodeClick}
                  textColor={TEXT}
                  textColor2={TEXT2}
                  panelBG={PANEL_BG}
                  printMode={printMode}
                />
              ))}
            </g>
          </svg>

          {/* Floating legend */}
          {showLegend && (
            <Legend
              visibleLevels={visibleLevels}
              setVisibleLevels={setVisibleLevels}
              printMode={printMode}
              onClose={() => setShowLegend(false)}
            />
          )}

          {/* Floating selection sidebar */}
          {selectedDetailList.length > 0 && (
            <SelectionSidebar
              list={selectedDetailList}
              onClear={() => { setSelectedNodes(new Set()); setSelectedInterfacesL(new Set()); }}
              onJumpToLLD={(id) => {
                if (nav?.goToInterface) nav.goToInterface(id);
                else onInterfaceSelect && onInterfaceSelect(id);
              }}
              printMode={printMode}
            />
          )}

          {/* Tip overlay (top-right) */}
          {!hoveredNode && !hoveredConnection && selectedNodes.size === 0 && (
            <div style={{
              position: "absolute", top: 14, right: 18,
              background: `${PANEL_BG}f0`,
              border: `1px solid ${PANEL_BORDER}`, borderRadius: 6,
              padding: "8px 12px", fontSize: 9, color: TEXT2, maxWidth: 280,
              fontFamily: FONTS.mono,
              boxShadow: printMode ? "0 2px 8px rgba(0,0,0,0.08)" : "0 4px 14px rgba(0,229,255,0.1)",
            }}>
              <div style={{ fontWeight: 700, color: T.cyan, marginBottom: 4 }}>HOW TO USE</div>
              <div>· <b>Click</b> a node to select it + its interfaces</div>
              <div>· <b>Alt+click</b> a node to focus (filters everything else)</div>
              <div>· <b>Ctrl+click</b> to multi-select</div>
              <div>· <b>Wheel</b> to zoom · <b>drag</b> to pan</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Toolbar
// ═══════════════════════════════════════════════════════════════════════════
function Toolbar(props) {
  const {
    viewPreset, setViewPreset,
    visibleLevels, setVisibleLevels,
    showAnimated, setShowAnimated,
    showAssociations, setShowAssociations,
    hideLegacyDirect, setHideLegacyDirect,
    showLegend, setShowLegend,
    printMode,
    boLens, setBoLens,
    focusNode, setFocusNode,
    zoom, setZoom, resetView, counts,
  } = props;
  const TEXT  = printMode ? "#0f172a" : T.text;
  const TEXT2 = printMode ? "#475569" : T.text2;
  const PBG   = printMode ? "#f4f6fa" : T.bg2;
  const PB    = printMode ? "#cbd5e1" : T.border;

  return (
    <div style={{
      display: "flex", gap: 10, padding: "10px 14px",
      background: PBG, borderBottom: `1px solid ${PB}`,
      alignItems: "center", flexWrap: "wrap",
      fontFamily: FONTS.body,
    }}>
      {/* Title + counts */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ fontFamily: FONTS.header, fontSize: 13, fontWeight: 700, color: T.cyan, letterSpacing: 1 }}>
          🗺 ARCHITECTURE
        </div>
        <div style={{ fontSize: 10, color: TEXT2 }}>
          {counts.ifaces}/{counts.total} interfaces
          {counts.selected > 0 && <span style={{ color: T.amber }}> · {counts.selected} selected</span>}
        </div>
      </div>

      <div style={{ width: 1, height: 22, background: PB }}/>

      {/* View preset */}
      <select value={viewPreset} onChange={e => setViewPreset(e.target.value)}
        data-interaction="1"
        style={{ ...selectSty(printMode), minWidth: 200 }}>
        {Object.entries(VIEW_PRESETS).map(([k, v]) => (
          <option key={k} value={k}>{v.label}</option>
        ))}
      </select>

      <div style={{ width: 1, height: 22, background: PB }}/>

      {/* Level chips */}
      {ISA95_LANES.map(l => (
        <button key={l.id}
          data-interaction="1"
          onClick={() => setVisibleLevels(prev => ({ ...prev, [l.id]: !prev[l.id] }))}
          style={{
            padding: "4px 10px", fontSize: 9, borderRadius: 12,
            background: visibleLevels[l.id] ? `${l.color}25` : "transparent",
            border: `1px solid ${visibleLevels[l.id] ? l.color : PB}`,
            color: visibleLevels[l.id] ? l.color : TEXT2,
            cursor: "pointer", fontFamily: FONTS.mono, fontWeight: 700, letterSpacing: 1,
          }}>
          {l.level}
        </button>
      ))}

      <div style={{ width: 1, height: 22, background: PB }}/>

      {/* Toggles */}
      <Toggle label="ESB-only" on={hideLegacyDirect} onToggle={() => setHideLegacyDirect(x => !x)} printMode={printMode}/>
      <Toggle label="Animate" on={showAnimated} onToggle={() => setShowAnimated(x => !x)} printMode={printMode}/>
      <Toggle label="Assoc." on={showAssociations} onToggle={() => setShowAssociations(x => !x)} printMode={printMode}/>
      <Toggle label="Legend" on={showLegend} onToggle={() => setShowLegend(x => !x)} printMode={printMode}/>

      <div style={{ width: 1, height: 22, background: PB }}/>

      {/* BO lens */}
      <select value={boLens} onChange={e => setBoLens(e.target.value)}
        data-interaction="1"
        style={selectSty(printMode)}
        title="Filter by business object (BO Lens)">
        <option value="">🔍 BO Lens · all</option>
        {ALL_BUSINESS_OBJECTS.map(bo => <option key={bo} value={bo}>{bo}</option>)}
      </select>

      {/* Focus indicator */}
      {focusNode && (
        <button onClick={() => setFocusNode(null)}
          data-interaction="1"
          style={{
            padding: "3px 10px", fontSize: 9, borderRadius: 12,
            background: `${T.cyan}25`, color: T.cyan, border: `1px solid ${T.cyan}`,
            cursor: "pointer", fontFamily: FONTS.mono, fontWeight: 700,
          }}>
          🎯 Focused: {focusNode} ✕
        </button>
      )}

      {/* Zoom controls (right) */}
      <div style={{ marginLeft: "auto", display: "flex", gap: 4, alignItems: "center" }}>
        <button data-interaction="1" onClick={() => setZoom(z => Math.max(0.25, z * 0.9))}
          style={btnSty(printMode)}>−</button>
        <span style={{ fontSize: 9, color: TEXT2, minWidth: 38, textAlign: "center", fontFamily: FONTS.mono }}>
          {Math.round(zoom * 100)}%
        </span>
        <button data-interaction="1" onClick={() => setZoom(z => Math.min(3, z * 1.1))}
          style={btnSty(printMode)}>+</button>
        <button data-interaction="1" onClick={resetView}
          style={{ ...btnSty(printMode), padding: "3px 8px", fontSize: 9 }}>Reset</button>
      </div>
    </div>
  );
}

function Toggle({ label, on, onToggle, printMode }) {
  const PB = printMode ? "#cbd5e1" : T.border;
  return (
    <label data-interaction="1" style={{
      fontSize: 9, color: on ? T.cyan : (printMode ? "#475569" : T.text2),
      display: "flex", alignItems: "center", gap: 4, cursor: "pointer",
      padding: "3px 9px", borderRadius: 11,
      background: on ? `${T.cyan}15` : "transparent",
      border: `1px solid ${on ? T.cyan : PB}`,
      fontFamily: FONTS.mono, fontWeight: on ? 700 : 400,
    }}>
      <input type="checkbox" checked={on} onChange={onToggle}
        style={{ display: "none" }}/>
      {on ? "✓" : "○"} {label}
    </label>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// NodeRender · the rendered SVG node with shape, label, badges
// ═══════════════════════════════════════════════════════════════════════════
function NodeRender({ nodeKey, node, state, hovered, selected, focused, boMatch, isFocusedRoot,
                      onHover, onClick, textColor, textColor2, panelBG, printMode }) {
  const { w, h } = getNodeSize(node);
  const x = node.x, y = node.y;

  let opacity = 1;
  if (!focused) opacity = 0.25;
  if (!boMatch) opacity = Math.min(opacity, 0.25);

  const stroke = selected || isFocusedRoot ? T.cyan : node.color;
  const strokeWidth = selected ? 3.5 : (hovered ? 2.5 : 1.5);
  const fill = printMode ? "#ffffff" : `${node.color}10`;

  // Progress bar at the bottom of the node
  const progressColor = state?.totalProgress >= 100 ? T.green :
                        state?.totalProgress >= 75  ? T.cyan  :
                        state?.totalProgress >= 50  ? T.amber :
                        state?.totalProgress > 0    ? T.orange : T.text3;

  return (
    <g
      data-node="1"
      transform={`translate(${x}, ${y})`}
      opacity={opacity}
      onClick={(e) => onClick(nodeKey, e)}
      onMouseEnter={() => onHover(nodeKey)}
      onMouseLeave={() => onHover(null)}
      style={{ cursor: "pointer" }}
      filter={hovered || isFocusedRoot ? "url(#vs-glow)" : undefined}
    >
      {/* Ambient heartbeat halo for nodes with active interfaces — gives the
          board a living, "operations-room" feel without being distracting. */}
      {state && state.totalIfaces > 0 && focused && (
        <NodeShape node={node} w={w} h={h}
          fill="none" stroke={node.color} strokeWidth={1.5}>
        </NodeShape>
      )}
      {state && state.inProgressCount > 0 && focused && (
        <g opacity={0.5}>
          <rect x={-w/2 - 4} y={-h/2 - 4} width={w + 8} height={h + 8} rx={12}
            fill="none" stroke={progressColor} strokeWidth="1.5">
            <animate attributeName="opacity" values="0.6;0.05;0.6" dur="2.4s" repeatCount="indefinite"/>
            <animate attributeName="stroke-width" values="1.5;3;1.5" dur="2.4s" repeatCount="indefinite"/>
          </rect>
        </g>
      )}

      <NodeShape node={node} w={w} h={h}
        fill={fill}
        stroke={stroke}
        strokeWidth={strokeWidth}
      />

      {/* Icon (top-left) */}
      <text x={-w/2 + 12} y={-h/2 + 22} fontSize="16">{node.icon}</text>

      {/* Label */}
      <text x={0} y={-h/2 + 28} textAnchor="middle"
        fontSize={node.role === "hub" ? 14 : 12}
        fontFamily={FONTS.header} fontWeight="700"
        fill={textColor}>
        {node.label}
      </text>

      {/* Sub-label */}
      {node.sub && (
        <text x={0} y={-h/2 + 44} textAnchor="middle"
          fontSize="9" fill={textColor2} fontStyle="italic">
          {node.sub}
        </text>
      )}

      {/* Version + node-key chip (bottom-right) */}
      <text x={w/2 - 8} y={h/2 - 8} fontSize="7"
        textAnchor="end" fill={textColor2} fontFamily={FONTS.mono} letterSpacing="1">
        {node.version ? `v${node.version} · ${nodeKey}` : nodeKey}
      </text>

      {/* Interface count badge (top-right) */}
      {state && state.totalIfaces > 0 && (
        <g transform={`translate(${w/2 - 22}, ${-h/2 + 14})`}>
          <circle r={11} fill={node.color} opacity={0.92}/>
          <text x={0} y={4} textAnchor="middle" fontSize="10"
            fontFamily={FONTS.mono} fontWeight="700" fill="#000">
            {state.totalIfaces}
          </text>
        </g>
      )}

      {/* High-risk indicator dot */}
      {state && state.highRiskCount > 0 && (
        <circle cx={w/2 - 4} cy={-h/2 + 4} r={4} fill={T.red}/>
      )}

      {/* Progress bar (bottom edge) */}
      {state && state.totalIfaces > 0 && node.role !== "bus" && (
        <g transform={`translate(${-w/2 + 12}, ${h/2 - 8})`}>
          <rect x={0} y={0} width={w - 24} height={3}
            rx={1.5} fill={printMode ? "#e2e8f0" : T.bg3}/>
          <rect x={0} y={0}
            width={(w - 24) * (state.totalProgress / 100)} height={3}
            rx={1.5} fill={progressColor}/>
        </g>
      )}
    </g>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ConnectionPath · the rendered SVG edge with a flowing data-particle stream
// ═══════════════════════════════════════════════════════════════════════════
function ConnectionPath({ edge, showAnimated, onHover, onInterfaceClick }) {
  // Stream speed scales (inversely) with data volume: busier links pulse faster.
  const baseDur = Math.max(2.2, 4.5 - Math.log2(edge.interfaces.length + 1) * 0.5);
  // 3 staggered particles give the impression of a continuous flowing stream.
  const particles = [0, 0.33, 0.66];
  const animate = showAnimated && edge.isActive && edge.flowType !== "association";

  return (
    <g>
      {/* Soft glow underlay for active links — gives the cinematic "lit" feel */}
      {edge.isActive && (
        <path d={edge.pathData}
          stroke={edge.flowColor} strokeWidth={edge.strokeWidth + 4}
          strokeOpacity={edge.opacity * 0.18} fill="none"
          strokeLinecap="round" style={{ filter: "blur(3px)" }}/>
      )}

      {/* Main edge line */}
      <path
        d={edge.pathData}
        stroke={edge.flowColor}
        strokeWidth={edge.strokeWidth}
        strokeOpacity={edge.opacity}
        strokeDasharray={edge.dashArray}
        fill="none"
        strokeLinecap="round"
        markerEnd={`url(#arr-${edge.category})`}
        onMouseEnter={() => onHover(`${edge.from}-${edge.to}`)}
        onMouseLeave={() => onHover(null)}
        style={{ cursor: edge.interfaces.length > 0 ? "pointer" : "default" }}
      />

      {/* Hidden path the particles follow */}
      {animate && <path id={`edge-path-${edge.key}`} d={edge.pathData} fill="none" stroke="none"/>}

      {/* Flowing particle stream — comet head + fading tail per particle */}
      {animate && particles.map((offset, i) => (
        <g key={i}>
          {/* tail (larger, fainter, slightly behind) */}
          <circle r={edge.isHovered ? 4.5 : 3.2} fill={edge.flowColor} opacity={edge.opacity * 0.25}>
            <animateMotion dur={`${baseDur}s`} repeatCount="indefinite" begin={`${offset * baseDur + 0.08}s`} keyPoints="0;1" keyTimes="0;1" calcMode="linear">
              <mpath href={`#edge-path-${edge.key}`}/>
            </animateMotion>
          </circle>
          {/* head (bright, glowing) */}
          <circle r={edge.isHovered ? 3 : 2.2} fill="#ffffff" opacity={edge.opacity}>
            <animateMotion dur={`${baseDur}s`} repeatCount="indefinite" begin={`${offset * baseDur}s`} keyPoints="0;1" keyTimes="0;1" calcMode="linear">
              <mpath href={`#edge-path-${edge.key}`}/>
            </animateMotion>
            <animate attributeName="r" values={`${edge.isHovered ? 3 : 2.2};${edge.isHovered ? 1.6 : 1.1};${edge.isHovered ? 3 : 2.2}`} dur={`${baseDur}s`} repeatCount="indefinite" begin={`${offset * baseDur}s`}/>
          </circle>
          {/* coloured halo around head */}
          <circle r={edge.isHovered ? 5 : 3.6} fill={edge.flowColor} opacity={edge.opacity * 0.5}>
            <animateMotion dur={`${baseDur}s`} repeatCount="indefinite" begin={`${offset * baseDur}s`} keyPoints="0;1" keyTimes="0;1" calcMode="linear">
              <mpath href={`#edge-path-${edge.key}`}/>
            </animateMotion>
          </circle>
        </g>
      ))}

      {/* Hover badge on midpoint */}
      {edge.isHovered && edge.interfaces.length > 0 && (
        <EdgeHoverBadge edge={edge} onInterfaceClick={onInterfaceClick}/>
      )}
    </g>
  );
}

function EdgeHoverBadge({ edge, onInterfaceClick }) {
  // Approximate midpoint via Bezier sampling
  const mx = (edge.fromNode.x + edge.toNode.x) / 2;
  const my = (edge.fromNode.y + edge.toNode.y) / 2;
  const list = edge.interfaces.slice(0, 6);
  const W = 200, H = 28 + list.length * 14;

  return (
    <g transform={`translate(${mx - W/2}, ${my - H/2})`} style={{ pointerEvents: "none" }}>
      <rect x={0} y={0} width={W} height={H} rx={4}
        fill="#081525e8" stroke={edge.flowColor} strokeWidth="1.5"/>
      <text x={8} y={16} fontSize="9" fontFamily={FONTS.mono}
        fill={edge.flowColor} fontWeight="700" letterSpacing="1">
        {edge.from} → {edge.to} · {FLOW_TYPES[edge.flowType]?.label}
      </text>
      {list.map((id, i) => (
        <text key={id} x={8} y={32 + i * 14} fontSize="9"
          fill={T.text} fontFamily={FONTS.mono}>
          {id}
        </text>
      ))}
      {edge.interfaces.length > list.length && (
        <text x={8} y={32 + list.length * 14} fontSize="8"
          fill={T.text3} fontStyle="italic">
          + {edge.interfaces.length - list.length} more
        </text>
      )}
    </g>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Legend · floating panel
// ═══════════════════════════════════════════════════════════════════════════
function Legend({ visibleLevels, setVisibleLevels, printMode, onClose }) {
  const PBG = printMode ? "#ffffff" : T.bg2;
  const PB  = printMode ? "#cbd5e1" : T.border;
  const TX  = printMode ? "#0f172a" : T.text;
  const TX2 = printMode ? "#475569" : T.text2;

  return (
    <div style={{
      position: "absolute", left: 14, bottom: 14,
      background: `${PBG}f6`, border: `1px solid ${PB}`,
      borderRadius: 8, padding: "10px 14px",
      maxWidth: 360, fontFamily: FONTS.body,
      boxShadow: printMode ? "0 2px 8px rgba(0,0,0,0.08)" : "0 4px 14px rgba(0,229,255,0.1)",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: T.cyan,
                       fontFamily: FONTS.mono, letterSpacing: 1 }}>
          LEGEND
        </span>
        <button data-interaction="1" onClick={onClose}
          style={{ background: "none", border: "none", color: TX2, cursor: "pointer", fontSize: 14, padding: 0 }}>×</button>
      </div>

      {/* Node-shape language */}
      <div style={{ fontSize: 8, color: TX2, letterSpacing: 1, marginBottom: 4, fontWeight: 600 }}>
        NODE SHAPES
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, marginBottom: 10 }}>
        {Object.entries(NODE_ROLES).map(([role, m]) => (
          <div key={role} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 9, color: TX }}>
            <svg width="34" height="22" viewBox="-17 -11 34 22">
              <NodeShape node={{ role }} w={28} h={18}
                fill={`${T.cyan}10`} stroke={T.cyan} strokeWidth={1.5}/>
            </svg>
            <span>{m.label}</span>
          </div>
        ))}
      </div>

      {/* Flow types */}
      <div style={{ fontSize: 8, color: TX2, letterSpacing: 1, marginBottom: 4, fontWeight: 600 }}>
        FLOW TYPES
      </div>
      {Object.entries(FLOW_TYPES).map(([k, v]) => (
        <div key={k} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 9, color: TX, marginBottom: 3 }}>
          <svg width="36" height="6" viewBox="0 0 36 6">
            <line x1="0" y1="3" x2="36" y2="3" stroke={T.cyan} strokeWidth="1.5"
              strokeDasharray={v.dash}/>
          </svg>
          <span style={{ minWidth: 90, fontFamily: FONTS.mono, fontSize: 8 }}>{v.label}</span>
          <span style={{ color: TX2, fontStyle: "italic", fontSize: 8 }}>{v.desc}</span>
        </div>
      ))}

      {/* Severity */}
      <div style={{ fontSize: 8, color: TX2, letterSpacing: 1, marginTop: 10, marginBottom: 4, fontWeight: 600 }}>
        EDGE PROGRESS COLOR
      </div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {[
          { c: T.green,  l: "100%" },
          { c: T.cyan,   l: "≥75%" },
          { c: T.amber,  l: "≥50%" },
          { c: T.orange, l: "≥25%" },
          { c: T.red,    l: "<25%" },
          { c: T.text3,  l: "filtered out" },
        ].map(s => (
          <span key={s.l} style={{
            fontSize: 8, fontFamily: FONTS.mono,
            color: s.c, padding: "1px 6px",
            background: `${s.c}15`, borderRadius: 8,
            border: `1px solid ${s.c}40`,
          }}>{s.l}</span>
        ))}
      </div>

      {/* ISA-95 levels */}
      <div style={{ fontSize: 8, color: TX2, letterSpacing: 1, marginTop: 10, marginBottom: 4, fontWeight: 600 }}>
        ISA-95 LEVELS (click to toggle)
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
        {ISA95_LANES.map(l => (
          <button key={l.id}
            data-interaction="1"
            onClick={() => setVisibleLevels(prev => ({ ...prev, [l.id]: !prev[l.id] }))}
            style={{
              padding: "2px 8px", fontSize: 8, borderRadius: 10,
              background: visibleLevels[l.id] ? `${l.color}25` : "transparent",
              border: `1px solid ${visibleLevels[l.id] ? l.color : PB}`,
              color: visibleLevels[l.id] ? l.color : TX2,
              cursor: "pointer", fontFamily: FONTS.mono, fontWeight: 700, letterSpacing: 1,
            }}>
            {l.level} · {l.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SelectionSidebar · right-side panel with all selected interfaces
// ═══════════════════════════════════════════════════════════════════════════
function SelectionSidebar({ list, onClear, onJumpToLLD, printMode }) {
  const PBG = printMode ? "#ffffff" : T.bg2;
  const PB  = printMode ? "#cbd5e1" : T.border;
  const TX  = printMode ? "#0f172a" : T.text;
  const TX2 = printMode ? "#475569" : T.text2;

  return (
    <div style={{
      position: "absolute", right: 14, top: 14, bottom: 14,
      width: 320, background: `${PBG}f8`,
      border: `1px solid ${PB}`, borderRadius: 8,
      padding: "12px 14px", overflowY: "auto",
      fontFamily: FONTS.body,
      boxShadow: printMode ? "0 2px 8px rgba(0,0,0,0.08)" : "0 4px 14px rgba(0,229,255,0.12)",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <span style={{ fontFamily: FONTS.mono, fontSize: 10, fontWeight: 700, color: T.cyan, letterSpacing: 1 }}>
          🎯 SELECTED · {list.length}
        </span>
        <button data-interaction="1" onClick={onClear} style={{
          background: "none", border: "none", color: TX2, cursor: "pointer",
          fontSize: 11, fontFamily: FONTS.mono,
        }}>clear</button>
      </div>

      {list.map(i => {
        const agents   = getAgentsForInterface(i.id);
        const benefits = getBenefitsForInterface(i.id);
        const flows    = getFlowsForInterface(i.id);
        return (
          <div key={i.id}
            data-interaction="1"
            onClick={() => onJumpToLLD(i.id)}
            style={{
              background: printMode ? "#f4f6fa" : T.bg3,
              border: `1px solid ${i.color}40`,
              borderLeft: `3px solid ${i.color}`,
              borderRadius: 6, padding: "8px 10px", marginBottom: 8,
              cursor: "pointer",
            }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
              <span style={{ fontFamily: FONTS.mono, fontSize: 10, color: i.color, fontWeight: 700 }}>
                {i.id}
              </span>
              <span style={{ fontSize: 8, color: TX2, fontFamily: FONTS.mono }}>
                MVP-{i.mvp} · {i.progress}%
              </span>
            </div>
            <div style={{ fontSize: 10, color: TX, marginBottom: 4 }}>{i.name}</div>
            <div style={{ fontSize: 8, color: TX2, fontFamily: FONTS.mono, marginBottom: 4 }}>
              {i.from} → {i.to}
            </div>
            {(agents.length > 0 || benefits.length > 0 || flows.length > 0) && (
              <div style={{ display: "flex", gap: 6, fontSize: 7, color: TX2 }}>
                {agents.length > 0 && <span>🤖 {agents.length}</span>}
                {benefits.length > 0 && <span>🎯 {benefits.length}</span>}
                {flows.length > 0 && <span>🚌 {flows.length}</span>}
              </div>
            )}
            <div style={{ fontSize: 7, color: T.cyan, marginTop: 4, fontStyle: "italic" }}>
              → click to open LLD
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── small helpers ────────────────────────────────────────────────────
const selectSty = (printMode) => ({
  background: printMode ? "#ffffff" : T.bg3,
  border: `1px solid ${printMode ? "#cbd5e1" : T.border}`,
  color: printMode ? "#0f172a" : T.text,
  padding: "4px 10px", borderRadius: 4, fontSize: 10,
  outline: "none", cursor: "pointer",
  fontFamily: FONTS.body,
});
const btnSty = (printMode) => ({
  padding: "3px 10px", fontSize: 12, borderRadius: 4, cursor: "pointer",
  background: printMode ? "#ffffff" : T.bg3,
  border: `1px solid ${printMode ? "#cbd5e1" : T.border}`,
  color: printMode ? "#0f172a" : T.text,
  fontFamily: FONTS.mono, fontWeight: 700,
});
